(() => {
  const beforeOpenTokens = new Set([
    'preorder_before_open',
    'preorder_deposit_before_open',
    'balance_payment_before_open',
  ]);
  const exactOpenTokens = new Set([
    'preorder_dated_open',
    'preorder_deposit_dated_open',
    'balance_payment_open',
  ]);
  const blockedTokens = new Set([...beforeOpenTokens, 'blocked']);
  const balanceTokens = new Set(['balance_payment_before_open', 'balance_payment_open']);
  const depositTokens = new Set([
    'preorder_deposit_before_open',
    'preorder_deposit_dated_open',
    'preorder_deposit_open_ended',
    'preorder_deposit_no_close_stated',
  ]);
  const timers = new WeakMap();

  function parseConfig(root) {
    const element = root.querySelector('[data-product-lifecycle-config]');
    if (!element) return null;
    try {
      const config = JSON.parse(element.textContent);
      if (!config.currentToken || !config.timeZone) return null;
      return config;
    } catch (_error) {
      return null;
    }
  }

  function timeZoneOffset(timestamp, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(timestamp));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return (
      Date.UTC(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day),
        Number(values.hour),
        Number(values.minute),
        Number(values.second),
      ) - timestamp
    );
  }

  function zonedMidnight(date, timeZone, addDays = 0) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return null;
    const [year, month, day] = date.split('-').map(Number);
    const target = Date.UTC(year, month - 1, day + addDays);
    let result = target;
    for (let iteration = 0; iteration < 3; iteration += 1) {
      result = target - timeZoneOffset(result, timeZone);
    }
    return result;
  }

  function resolvedToken(config, now = Date.now()) {
    let token = config.currentToken;
    if (beforeOpenTokens.has(token)) {
      const openAt = zonedMidnight(config.preorderOpenDate, config.timeZone);
      if (openAt === null || !config.postOpenToken) return 'needs_review';
      if (now >= openAt) token = config.postOpenToken;
    }
    if (exactOpenTokens.has(token) && config.preorderCloseDate) {
      const closesAfter = zonedMidnight(config.preorderCloseDate, config.timeZone, 1);
      if (closesAfter === null) return 'needs_review';
      if (now >= closesAfter) token = 'blocked';
    }
    return token;
  }

  function nextBoundary(config, token, now = Date.now()) {
    if (beforeOpenTokens.has(token)) {
      return zonedMidnight(config.preorderOpenDate, config.timeZone);
    }
    if (exactOpenTokens.has(token) && config.preorderCloseDate) {
      return zonedMidnight(config.preorderCloseDate, config.timeZone, 1);
    }
    return null;
  }

  function setOriginalDisabled(element) {
    if (!element.hasAttribute('data-lifecycle-original-disabled')) {
      element.dataset.lifecycleOriginalDisabled = element.disabled ? 'true' : 'false';
    }
  }

  function updateRoot(root) {
    const config = parseConfig(root);
    if (!config) return;
    const token = resolvedToken(config);
    root.dataset.lifecycleToken = token;

    root.querySelectorAll('[data-lifecycle-message]').forEach((message) => {
      message.hidden = message.dataset.token !== token;
    });
    root.querySelectorAll('[data-lifecycle-button-label]').forEach((label) => {
      let text = label.dataset.labelDefault;
      if (beforeOpenTokens.has(token)) text = label.dataset.labelNotOpen;
      else if (token === 'blocked') text = label.dataset.labelUnavailable;
      else if (depositTokens.has(token)) text = label.dataset.labelDeposit;
      else if (token === 'balance_payment_open') text = label.dataset.labelBalance;
      else if (token.startsWith('preorder_')) text = label.dataset.labelPreorder;
      if (text && label.textContent !== text) label.textContent = text;
    });

    const blocked = blockedTokens.has(token);
    root
      .querySelectorAll('[data-lifecycle-add], [data-lifecycle-variant-input], button[name="add"]')
      .forEach((control) => {
      setOriginalDisabled(control);
      control.disabled = blocked || control.dataset.lifecycleOriginalDisabled === 'true';
      control.setAttribute('aria-disabled', control.disabled ? 'true' : 'false');
    });
    root.querySelectorAll('[data-lifecycle-plus], button[name="plus"]').forEach((control) => {
      setOriginalDisabled(control);
      control.disabled = blocked || control.dataset.lifecycleOriginalDisabled === 'true';
    });
    root.querySelectorAll('[data-lifecycle-quantity], input[name="quantity"], input.quantity__input').forEach((input) => {
      if (!input.hasAttribute('data-lifecycle-original-max')) {
        input.dataset.lifecycleOriginalMax = input.getAttribute('max') || '';
      }
      if (blocked) input.max = input.value;
      else if (input.dataset.lifecycleOriginalMax) input.max = input.dataset.lifecycleOriginalMax;
      else input.removeAttribute('max');
    });
    root.querySelectorAll('[data-lifecycle-dynamic-checkout]').forEach((checkout) => {
      checkout.hidden = blocked || balanceTokens.has(token) || depositTokens.has(token);
    });

    const oldTimer = timers.get(root);
    if (oldTimer) window.clearTimeout(oldTimer);
    const boundary = nextBoundary(config, token);
    if (boundary && boundary > Date.now()) {
      const delay = Math.min(boundary - Date.now() + 100, 2147483647);
      timers.set(
        root,
        window.setTimeout(() => {
          updateRoot(root);
          updateCheckoutGates();
        }, delay),
      );
    }
  }

  function updateCheckoutGates() {
    const hasBlockedLine = [...document.querySelectorAll('[data-lifecycle-cart-line]')].some((line) =>
      blockedTokens.has(line.dataset.lifecycleToken),
    );
    document.querySelectorAll('[data-lifecycle-cart-checkout]').forEach((checkout) => {
      checkout.dataset.lifecycleCheckoutBlocked = hasBlockedLine ? 'true' : 'false';
      checkout.querySelectorAll('button[name="checkout"]').forEach((button) => {
        setOriginalDisabled(button);
        button.disabled = hasBlockedLine || button.dataset.lifecycleOriginalDisabled === 'true';
      });
      checkout.querySelectorAll('.additional-checkout-buttons, .cart__dynamic-checkout-buttons').forEach((element) => {
        element.hidden = hasBlockedLine;
      });
    });
  }

  function updateAll(scope = document) {
    scope.querySelectorAll('[data-product-lifecycle-root]').forEach(updateRoot);
    updateCheckoutGates();
  }

  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      updateAll();
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateAll();
    observer.observe(document.body, { childList: true, subtree: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) updateAll();
  });
  window.addEventListener('pageshow', () => updateAll());

  window.HeatovaProductLifecycle = { resolvedToken, zonedMidnight, updateAll };
})();
